import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SECTION_IDS = ['biography', 'experience', 'practice', 'writing', 'credentials'];

const Navbar = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState('');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const isHome = location.pathname === '/';

  const links = [
    { id: 'biography',   label: t.nav.biography,   type: 'section' },
    { id: 'experience',  label: t.nav.experience,  type: 'section' },
    { id: 'practice',    label: t.nav.practice,    type: 'section' },
    { id: 'writing',     label: t.nav.writing,     type: 'section' },
    { id: 'credentials', label: t.nav.credentials, type: 'section' },
    { id: 'retainr',     label: t.nav.retainr,     type: 'page', path: '/retainr' },
    { id: 'contact',     label: t.nav.contact,     type: 'page', path: '/contact' },
  ];

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      if (!isHome) return;
      const y = window.scrollY + 200;
      let current = '';
      SECTION_IDS.forEach((id) => {
        const s = document.getElementById(id);
        if (s && s.offsetTop <= y) current = id;
      });
      setActive(current);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const go = (link) => {
    setMobileOpen(false);
    if (link.type === 'page') {
      navigate(link.path);
      return;
    }
    if (isHome) {
      document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/', { state: { scrollTo: link.id } });
    }
  };

  const goTop = () => {
    setMobileOpen(false);
    if (isHome) window.scrollTo({ top: 0, behavior: 'smooth' });
    else navigate('/');
  };

  const isActive = (l) => (l.type === 'page' ? location.pathname === l.path : isHome && active === l.id);

  return (
    <>
      <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || !isHome ? 'navbar-frost' : 'bg-transparent'
        }`}
      >
        <div className="container-custom flex items-center justify-between py-5">
          <button onClick={goTop} className="text-left" aria-label="Top" data-testid="nav-home-link">
            <div className="font-heading text-xl md:text-2xl tracking-[0.2em] text-[#C4A35A] whitespace-nowrap">
              K.C. REDDING
            </div>
            <div className="text-[10px] tracking-[0.35em] text-[#8a7a54] mt-1 whitespace-nowrap">
              IMMIGRATION &middot; FAMILY LAW
            </div>
          </button>

          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l)}
                data-testid={`nav-link-${l.id}`}
                className={`nav-link text-[11px] tracking-[0.3em] uppercase text-[#DCCFAD] hover:text-[#C4A35A] transition-colors ${
                  isActive(l) ? 'active text-[#C4A35A]' : ''
                }`}
              >
                {l.label}
              </button>
            ))}

            <div className="relative">
              <button
                onClick={() => setLangMenuOpen((o) => !o)}
                onBlur={() => setTimeout(() => setLangMenuOpen(false), 150)}
                data-testid="lang-toggle"
                className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#DCCFAD] hover:text-[#C4A35A] transition-colors border border-[rgba(196,163,90,0.25)] px-3 py-1.5 rounded-sm"
              >
                <Globe size={13} />
                {lang.toUpperCase()}
              </button>
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 top-full mt-2 w-44 bg-[#0f1422] border border-[rgba(196,163,90,0.2)] rounded-sm shadow-xl overflow-hidden"
                  >
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setLang(l.code); setLangMenuOpen(false); }}
                        data-testid={`lang-option-${l.code}`}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-[11px] tracking-[0.25em] uppercase text-left hover:bg-[rgba(196,163,90,0.08)] transition-colors ${
                          lang === l.code ? 'text-[#C4A35A]' : 'text-[#DCCFAD]'
                        }`}
                      >
                        <span className="text-base leading-none">{l.flag}</span>
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <button
            className="lg:hidden text-[#C4A35A]"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu size={28} />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] bg-[#0A0E1A]/98 backdrop-blur-lg flex flex-col"
          >
            <div className="flex items-center justify-between p-6">
              <div className="font-heading text-lg tracking-[0.2em] text-[#C4A35A]">
                K.C. REDDING
              </div>
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="text-[#C4A35A]" data-testid="mobile-menu-close">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-7">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(l)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="font-heading text-3xl text-[#F0EDE8] hover:text-[#C4A35A] transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <div className="gold-rule mt-6" />
              <div className="flex gap-3 mt-2">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`px-4 py-2 text-xs tracking-[0.3em] uppercase border rounded-sm transition-colors ${
                      lang === l.code
                        ? 'border-[#C4A35A] text-[#C4A35A] bg-[rgba(196,163,90,0.08)]'
                        : 'border-[rgba(196,163,90,0.25)] text-[#DCCFAD]'
                    }`}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
